import { Link } from "react-router-dom";
import PublicShell, { useSiteContent } from "@/components/layout/PublicShell";
import { ArrowRight, Activity, ShieldCheck, BarChart3, Workflow, Cpu, Sparkles } from "lucide-react";

const ICONS = [Activity, ShieldCheck, BarChart3, Workflow, Cpu, Sparkles];

export default function AIInDeliveryPage() {
  const content = useSiteContent();
  const ai = content?.ai_in_delivery || {};
  const caps = ai.capabilities || [];

  return (
    <PublicShell>
      <section className="relative border-b border-border bg-obsidian text-white overflow-hidden" data-testid="ai-hero">
        <div className="absolute inset-0 bg-gridlines-dark opacity-60 pointer-events-none" />
        <div className="absolute inset-0 bg-radial-blue pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 lg:pt-28 pb-16 lg:pb-24">
          <div className="flex items-center gap-3 mb-6">
            <span className="status-dot" />
            <span className="overline-light">AI · Embedded in delivery</span>
          </div>
          <h1 className="font-heading text-5xl lg:text-7xl font-bold tracking-tighter leading-[1] max-w-5xl">
            {ai.page_title || "AI in Delivery"}
          </h1>
          <p className="mt-6 text-lg lg:text-2xl text-white/75 max-w-3xl">{ai.page_subtitle}</p>
          <div className="mt-10 max-w-3xl text-base text-white/70 leading-relaxed">{ai.intro}</div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { label: "Decision cycles", v: "Faster" },
              { label: "Risk detection", v: "Earlier" },
              { label: "Reporting time", v: "Lower" },
              { label: "Capability match", v: "Better" },
            ].map((s) => (
              <div key={s.label} className="border border-white/15 bg-white/[0.03] p-4">
                <div className="font-heading text-xl lg:text-2xl font-semibold">{s.v}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/60 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section" data-testid="ai-capabilities">
        <div className="overline mb-4">Capabilities</div>
        <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05] max-w-3xl mb-10 lg:mb-14">
          Where AI runs alongside our delivery teams.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border border-border">
          {caps.map((c, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div
                key={c.key || i}
                className={`p-6 lg:p-8 ${i % 3 !== 0 ? "lg:border-l border-border" : ""} ${i % 2 !== 0 ? "md:border-l border-border lg:border-l-0 lg:border-l" : ""} border-b border-border last:border-b-0 hover:bg-muted transition-colors group`}
                data-testid={`ai-capability-${c.key || i}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <Icon className="h-6 w-6 text-execution" />
                  <span className="text-[10px] font-mono tracking-[0.2em] text-muted-foreground">AI_0{i + 1}</span>
                </div>
                <h3 className="font-heading text-xl lg:text-2xl font-semibold tracking-tight mb-3">{c.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-border bg-muted" data-testid="ai-outcomes">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Outcomes</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
              {ai.outcome_title || "Outcomes you can measure"}
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-border border-t border-b border-border">
              {(ai.outcomes || []).map((o, i) => (
                <li key={o} className="py-6 flex gap-6 items-start">
                  <span className="font-mono text-xs text-execution tracking-widest w-10 shrink-0 pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-lg lg:text-xl font-heading font-medium leading-snug">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-obsidian text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <h3 className="md:col-span-8 font-heading text-3xl lg:text-5xl font-bold tracking-tighter leading-[1.02]">
            AI doesn't deliver programs. Delivery teams do — with AI alongside them.
          </h3>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/engage" className="btn-accent">
              Engage Nexra
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
